import { useState, useMemo, useCallback } from 'react';
import { Combobox, InputBase, useCombobox, Group, ScrollArea, CloseButton } from '@mantine/core';
import { IconAlertTriangle } from '../../../icons';
import { CountryFlag } from '../../utils/CountryFlag';
import {
  filterCountriesBySearch,
  getCountryNameByCode,
  normalizeCountryCode,
} from '../../../services/countries';
import styles from './CountrySelect.module.css';

export const CountrySelect = ({
  label,
  error,
  required,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled,
}) => {
  const [search, setSearch] = useState('');

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
      onBlur?.();
    },
    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
  });

  const filteredOptions = useMemo(() => filterCountriesBySearch(search), [search]);

  const handleOptionSubmit = useCallback(
    (code) => {
      onChange(normalizeCountryCode(code));
      combobox.closeDropdown();
    },
    [onChange, combobox]
  );

  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      onChange('');
    },
    [onChange]
  );

  // The clear button lives in the right section, a sibling of the button rather
  // than a child, so disabling the button does not reach it: it has to be left
  // out, or a disabled field could still be emptied through it.
  const canClear = Boolean(value) && !error && !disabled;

  const normalizedValue = normalizeCountryCode(value);
  const selectedLabel = normalizedValue
    ? getCountryNameByCode(normalizedValue, normalizedValue)
    : null;

  const options = filteredOptions.map((opt) => (
    <Combobox.Option
      value={opt.value}
      key={opt.value}
      active={opt.value === normalizedValue}
    >
      <Group gap="xs" wrap="nowrap" align="center">
        <CountryFlag countryCode={opt.value} />
        <span className={styles.optionLabel}>{opt.label}</span>
      </Group>
    </Combobox.Option>
  ));

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleOptionSubmit}
      withinPortal
    >
      {/*
        The label is rendered by the InputBase below, inside the same wrapper as the
        button. Mantine ties a label to the input of the nearest wrapper, so a label
        drawn by an outer Input.Wrapper would point at an id no element carries:
        clicking it would not open the picker, and assistive technologies would not
        read it.
      */}
      <Combobox.Target>
        <InputBase
          component="button"
          type="button"
          pointer
          label={label}
          required={required}
          leftSection={
            normalizedValue ? <CountryFlag countryCode={normalizedValue} size="sm" /> : null
          }
          rightSection={
            error ? (
              <IconAlertTriangle size={18} stroke={1.5} className="input-error-icon" />
            ) : canClear ? (
              <CloseButton size="sm" onMouseDown={handleClear} aria-label="Clear selection" />
            ) : (
              <Combobox.Chevron />
            )
          }
          rightSectionPointerEvents={canClear ? 'all' : 'none'}
          disabled={disabled}
          onClick={() => combobox.toggleDropdown()}
          classNames={{
            root: styles.root,
            input: styles.input,
          }}
        >
          {selectedLabel ? (
            <span className={styles.selectedValue}>{selectedLabel}</span>
          ) : (
            <span className={styles.placeholder}>{placeholder || 'Select country'}</span>
          )}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown className={styles.dropdown}>
        <Combobox.Search
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder="Search country..."
        />
        <ScrollArea.Autosize mah={260} type="scroll">
          <Combobox.Options>
            {options.length > 0 ? options : <Combobox.Empty>No country found</Combobox.Empty>}
          </Combobox.Options>
        </ScrollArea.Autosize>
      </Combobox.Dropdown>
    </Combobox>
  );
};
